import { computed, defineComponent, Teleport } from 'vue'

import BaseModal from '@/components/base/modal/BaseModal'
import MenuButton from '@/components/common/button/MenuButton'
import VIcon from '@/components/common/VIcon'
import useMenuModal from '@/composables/common/modal/useMenuModal'
import { displayItem } from '@/utils/item'

interface Props {
  tData?: string
  modalkey: symbol
  title?: string
  class?: string
  value?: Record<string, any> | string | null
  items?: Array<Record<string, any> | string>
  itemText?: string
  itemKey?: string
  highlightEnabled?: boolean
  onInput?: (value: any) => void
}

export default defineComponent<Props>((props, context) => {
  const mainModal = useMenuModal(props.modalkey)

  // #region Props Getters

  const tData = computed((): string => props.tData ?? 'menu-modal')
  const itemText = computed(() => props.itemText ? props.itemText : 'text')
  const itemKey = computed(() => props.itemKey ? props.itemKey : 'value')

  // #endregion

  const currentKeyValue = computed(() => displayItem(props.value, itemKey.value))

  const modalMenuList = computed((): JSX.Element[] | undefined => {
    return props.items?.map((item, index): JSX.Element => {
      const key = typeof item === 'string' ? item : item[itemKey.value]
      return (
        <MenuButton
          key={`menu-modal-item-${index}-${key}`}
          t-data={`${props.tData}-menu-item`}
          checked={props.highlightEnabled && key === currentKeyValue.value}
          onClick={() => props.onInput?.(item)}
        >
          {context.slots.item === undefined
            ? <span>{displayItem(item, itemText.value)}</span>
            : context.slots.item({ itemText: displayItem(item, itemText.value), item })}
        </MenuButton>
      )
    })
  })

  // #endregion

  return () => (
    <Teleport to='body'>
      <BaseModal
        t-data={tData.value}
        modalkey={mainModal.key}
      >
        <div class={['flex w-screen flex-col bg-white pb-2.5', props.class]}>
          <div class='flex h-12 w-full items-center justify-center border-b border-neutral-300 px-4 py-3'>
            <h1 class='flex-1 text-subtitle-1'>{props.title}</h1>
            <button
              t-data='menu-modal-close-btn'
              onClick={() => { mainModal.cancel() }}
            >
              <VIcon
                class='text-[24px]'
                icon='material-symbols:close'
              />
            </button>
          </div>
          <div class='mac-scrollbar h-full flex-1 overflow-y-auto'>
            {modalMenuList.value}
            {context.slots.default?.()}
          </div>
        </div>
      </BaseModal>
    </Teleport>
  )
}, {
  name: 'MenuModal',
  props: [
    'tData',
    'modalkey',
    'title',
    'class',
    'value',
    'items',
    'itemText',
    'itemKey',
    'highlightEnabled',
    'onInput'
  ]
})
